import { useEffect, useState } from 'react';
import { passbookLogoUrl, passbookTierLogoUrl } from '@/shared/lib/passbook-utils';
import type { PublicInvitePassbook } from '@/features/public-invite/api/use-public-invite';
import type { ParticipantTier } from '../api/participant-api';

interface WalletCardPreviewProps {
  passbook: PublicInvitePassbook;
  /** Nome de quem é o cartão (área logada). */
  holderName?: string;
  /** Saldo já formatado ("1.250 pontos"). */
  balance?: string;
  serialNumber?: string;
  /** Cores, logo e rótulo do nível — como no Wallet. */
  tier?: ParticipantTier | null;
}

/**
 * Prévia do cartão como ele aparece na carteira do celular: cor de fundo,
 * rótulos e texto que a loja configurou para o passe (Apple/Google Wallet).
 * Com `hasTiers`, as cores e o nome do nível vêm do tier atual.
 */
export function WalletCardPreview({ passbook, holderName, balance, serialNumber, tier }: WalletCardPreviewProps) {
  const [tierLogoFailed, setTierLogoFailed] = useState(false);
  useEffect(() => {
    setTierLogoFailed(false);
  }, [tier?.level, passbook.idPassbook]);
  const background = tier?.backgroundColor || passbook.backgroundColor || 'hsl(var(--primary))';
  const foreground = tier?.foregroundColor || passbook.foregroundColor || '#fff';
  const label = tier?.labelColor || passbook.labelColor || foreground;
  const lastDigits = serialNumber ? serialNumber.replace(/[^0-9A-Za-z]/g, '').slice(-4).toUpperCase() : '';
  const logoSrc =
    tier && !tierLogoFailed ? passbookTierLogoUrl(passbook.idPassbook, tier.level) : passbookLogoUrl(passbook.idPassbook);

  return (
    <div
      className="relative mx-auto flex aspect-[1.586/1] w-full max-w-sm flex-col justify-between overflow-hidden rounded-[1.25rem] p-5 shadow-glass-lg ring-1 ring-black/10"
      style={{ backgroundColor: background, color: foreground }}
      aria-label={`Cartão fidelidade ${passbook.organizationName}`}
    >
      {/* brilho de "plástico" em degradê radial (sem `blur`: no iOS o filtro vaza pelo arredondado) */}
      <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_95%_0%,rgba(255,255,255,0.16),transparent_45%),radial-gradient(circle_at_0%_110%,rgba(0,0,0,0.16),transparent_50%)]" />

      <div className="relative flex items-start justify-between gap-3">
        <img
          src={logoSrc}
          alt={passbook.organizationName}
          className="h-9 max-w-[55%] object-contain object-left"
          onError={(e) => {
            if (tier && !tierLogoFailed) {
              setTierLogoFailed(true);
              return;
            }
            e.currentTarget.style.display = 'none';
          }}
        />
        <span className="text-right text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: label }}>
          {tier?.label || 'Cartão fidelidade'}
        </span>
      </div>

      <div className="relative">
        {balance ? (
          <>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: label }}>
              Saldo
            </p>
            <p className="text-3xl font-semibold tabular-nums leading-tight">{balance}</p>
          </>
        ) : (
          <p className="text-xl font-semibold leading-tight">{passbook.organizationName}</p>
        )}
      </div>

      <div className="relative flex items-end justify-between gap-3">
        <div className="min-w-0">
          {holderName ? (
            <>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: label }}>
                Nome
              </p>
              <p className="truncate text-sm font-medium">{holderName}</p>
            </>
          ) : null}
        </div>
        {lastDigits ? (
          <p className="shrink-0 font-mono text-xs tracking-widest opacity-80">•••• {lastDigits}</p>
        ) : null}
      </div>
    </div>
  );
}
